// [목적] Follow/Unfollow 토글 — 낙관적 업데이트 + 실패 시 롤백 + Toast 알림
// [이유] FEED-02 — ProfilePage 팔로우 버튼 클릭 시 즉각 UI 반응(낙관적) + 실패 롤백 보장.
//         본인(isMe=true) 이면 팔로우 버튼 자체를 숨기므로 훅 레벨에서 isMe 분기는 불필요
// [사이드 이팩트] useMutation 성공/실패 시 useToast().show() 호출.
//                 낙관적 업데이트: queryKeys.user.profile(handle) 캐시의 isFollowing/followerCount 즉시 변경.
//                 실패 시 onError 에서 이전 캐시 값으로 rollback.
//                 완료(onSettled) 후 profile 쿼리 invalidate 로 서버 최신값 동기화.
// [수정 시 고려사항] follow/unfollow API 는 모두 JwtAuthGuard + CsrfGuard — http.ts 가 CSRF 헤더 자동 첨부.
//                   낙관적 업데이트 context 타입을 명시해 rollback 안전성 확보.
//                   게스트 상태에서 호출 시 requireAuth() 로 로그인 유도 (훅 내 처리)

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { follow, unfollow } from '../api/user.client.js';
import { queryKeys } from '../api/queryKeys.js';
import { useToast } from '../components/feedback/Toast.js';
import { useAuth } from './useAuth.js';
import type { UserPublicProfile } from '@gangwoncanvas/types';

// ─── 낙관적 업데이트 컨텍스트 타입 ──────────────────────────────────────────

interface OptimisticContext {
  previousProfile: UserPublicProfile | undefined;
}

// ─── useFollowToggle ─────────────────────────────────────────────────────────

/**
 * [목적] Follow/Unfollow 토글 mutation — 낙관적 업데이트 + 롤백
 * [이유] FEED-02 ProfilePage 팔로우 버튼 — 즉각 UI 반응 + 서버 동기화
 * [사이드 이팩트] profile 캐시 즉시 변경 → 실패 시 이전 값 복원 → 완료 후 invalidate
 * [수정 시 고려사항] handle 이 undefined 이면 mutation 자체를 호출하지 말 것 (enabled 분기는 컴포넌트 책임)
 */
export function useFollowToggle(handle: string): {
  toggle: (currentlyFollowing: boolean) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { show } = useToast();
  const { status, requireAuth } = useAuth();

  const mutation = useMutation<
    Awaited<ReturnType<typeof follow>>,
    Error,
    boolean, // variables: currentlyFollowing
    OptimisticContext
  >({
    /**
     * [목적] follow/unfollow API 선택 후 호출
     * [이유] currentlyFollowing 값으로 POST/DELETE 분기
     * [사이드 이팩트] JwtAuthGuard 필수 — 401 시 ApiError throw
     * [수정 시 고려사항] alreadyFollowing=true 응답은 무시(idempotent)
     */
    mutationFn: (currentlyFollowing: boolean) =>
      currentlyFollowing ? unfollow(handle) : follow(handle),

    /**
     * [목적] 낙관적 업데이트 — API 응답 전에 캐시 즉시 변경
     * [이유] UX 즉각 반응. 실패 시 onError 에서 context.previousProfile 로 rollback
     * [사이드 이팩트] profile 캐시의 isFollowing + followerCount 즉시 변경
     * [수정 시 고려사항] cancelQueries 로 진행 중인 refetch 와 충돌 방지
     */
    onMutate: (currentlyFollowing: boolean): OptimisticContext => {
      // [목적] 낙관적 업데이트는 동기적으로 적용해 호출 직후 캐시 검증이 가능하도록 보장
      // [이유] async + cancelQueries(await) 시 onMutate 가 마이크로태스크로 밀려 테스트가 즉시 캐시를 검증할 수 없음
      // [사이드 이팩트] cancelQueries 를 비동기 fire-and-forget 으로 호출 — 진행 중 refetch 와 race 가능성 미소
      // [수정 시 고려사항] race 위험이 커지면 onMutate 를 다시 async 로 복원하고 테스트를 waitFor 패턴으로 수정
      void queryClient.cancelQueries({ queryKey: queryKeys.user.profile(handle) });

      const previousProfile = queryClient.getQueryData<UserPublicProfile>(
        queryKeys.user.profile(handle),
      );

      // 낙관적 업데이트 적용
      if (previousProfile) {
        queryClient.setQueryData<UserPublicProfile>(queryKeys.user.profile(handle), {
          ...previousProfile,
          isFollowing: !currentlyFollowing,
          followerCount: currentlyFollowing
            ? Math.max(0, previousProfile.followerCount - 1)
            : previousProfile.followerCount + 1,
        });
      }

      return { previousProfile };
    },

    /**
     * [목적] 실패 시 이전 캐시 값으로 rollback
     * [이유] 서버 에러 발생 시 UI 가 잘못된 상태로 남지 않도록 보장
     * [사이드 이팩트] previousProfile 이 없으면 rollback 없음 (캐시 미탑재 경우)
     * [수정 시 고려사항] Toast 에러 메시지 표시
     */
    onError: (_err, _currentlyFollowing, context): void => {
      if (context?.previousProfile) {
        queryClient.setQueryData<UserPublicProfile>(
          queryKeys.user.profile(handle),
          context.previousProfile,
        );
      }
      show('팔로우 처리 중 오류가 발생했습니다.', 'error');
    },

    /**
     * [목적] 완료(성공/실패 무관) 후 서버 최신값으로 캐시 동기화
     * [이유] 낙관적 업데이트 값과 서버 실제 값의 불일치 해소
     * [사이드 이팩트] profile 쿼리 invalidate → 백그라운드 refetch 트리거
     * [수정 시 고려사항] onSettled 는 rollback 이후에 실행됨
     */
    onSettled: (): void => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(handle) });
    },

    /**
     * [목적] 성공 시 Toast 메시지 표시
     * [이유] 사용자에게 팔로우 상태 변경을 명시적으로 알림
     * [사이드 이팩트] useToast show() 호출
     * [수정 시 고려사항] variables 는 mutation 호출 시 전달된 currentlyFollowing 값
     */
    onSuccess: (_data, currentlyFollowing): void => {
      show(currentlyFollowing ? '팔로우를 취소했습니다.' : '팔로우했습니다!', 'success');
    },
  });

  /**
   * [목적] 외부 토글 진입점 — guest 차단 + mutation 호출
   * [이유] 컴포넌트에서 단순히 toggle(isFollowing) 으로 호출
   * [사이드 이팩트] guest 상태면 requireAuth() 로 /login redirect
   * [수정 시 고려사항] isPending 중 중복 클릭 방지는 컴포넌트 disabled 처리 책임
   */
  const toggle = (currentlyFollowing: boolean): void => {
    if (status !== 'authenticated') {
      requireAuth();
      return;
    }
    mutation.mutate(currentlyFollowing);
  };

  return { toggle, isPending: mutation.isPending };
}
